// PO1-3303 (H1) — regras puras da cotação "por caixas" na Cotação Individual.
//
// Uma linha da tela = { id, quantidade, peso, altura, largura, comprimento }
// como texto digitado (pt-BR). O peso é de UMA caixa (kg) e as medidas em cm.
// Quantidade de volumes, peso total e m³ são derivados daqui só para mostrar:
// o backend recalcula a partir da mesma lista (hub-backend utils/caixas), então
// nada do preço depende da soma feita na tela.
//
// Compatível com o build em Node 12 (CRA 3.4): sem ?. nem ??.

export const MODO_CARGA = { TOTAL: "total", CAIXAS: "caixas" };

export const OBSERVACAO_PELO_TOTAL =
  "Calculado pelo peso total, sem as medidas dos volumes";

const CAMPOS_MEDIDA = ["peso", "altura", "largura", "comprimento"];

export const caixaVazia = (id) => ({
  id,
  quantidade: "1",
  peso: "",
  altura: "",
  largura: "",
  comprimento: "",
});

// Mesma leitura de número da tela (pt-BR: "1.234,5"; também aceita "2.5").
export function parseNumeroBR(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/\s/g, "").replace(/[^\d.,-]/g, "");
  if (!cleaned) return 0;
  let normalized = cleaned;
  if (cleaned.indexOf(",") !== -1) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.split(".").length > 2) {
    const parts = cleaned.split(".");
    const last = parts.pop();
    normalized = `${parts.join("")}.${last}`;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

// Formata sem depender do ICU do ambiente (toLocaleString pt-BR falha no Node
// sem ICU completo e deixaria o Jest com saída diferente do navegador).
export function formatarNumeroBR(n, casas) {
  const fixo = Number(n || 0).toFixed(casas);
  const partes = fixo.split(".");
  const inteiro = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return partes.length > 1 ? `${inteiro},${partes[1]}` : inteiro;
}

export function caixaValida(caixa) {
  if (!caixa) return false;
  const quantidade = parseNumeroBR(caixa.quantidade);
  if (!Number.isInteger(quantidade) || quantidade < 1) return false;
  return CAMPOS_MEDIDA.every((campo) => parseNumeroBR(caixa[campo]) > 0);
}

export function normalizarCaixa(caixa) {
  return {
    quantidade: parseNumeroBR(caixa.quantidade),
    peso: parseNumeroBR(caixa.peso),
    altura: parseNumeroBR(caixa.altura),
    largura: parseNumeroBR(caixa.largura),
    comprimento: parseNumeroBR(caixa.comprimento),
  };
}

// Todas as linhas preenchidas e válidas (uma linha pela metade bloqueia, como
// um serviço incompleto bloqueia o salvar).
export function caixasCompletas(lista) {
  return Array.isArray(lista) && lista.length > 0 && lista.every(caixaValida);
}

// O que vai no payload: as linhas válidas, em número. Sem agrupar: quem agrupa
// caixas iguais é o backend.
export function caixasParaPayload(lista) {
  return (lista || []).filter(caixaValida).map(normalizarCaixa);
}

const arredondar = (valor, casas) => {
  const fator = 10 ** casas;
  return Math.round(valor * fator) / fator;
};

// Totais para a tela: grupos de caixas iguais, quantidade de volumes, peso
// total e m³ (mesma conta do backend).
export function totaisCaixas(lista) {
  const grupos = [];
  let linhasValidas = 0;
  (lista || []).forEach((caixa) => {
    if (!caixaValida(caixa)) return;
    linhasValidas += 1;
    const n = normalizarCaixa(caixa);
    const igual = grupos.find(
      (g) =>
        g.peso === n.peso &&
        g.altura === n.altura &&
        g.largura === n.largura &&
        g.comprimento === n.comprimento
    );
    if (igual) {
      igual.quantidade += n.quantidade;
    } else {
      grupos.push(n);
    }
  });
  const qtdVolumes = grupos.reduce((acc, g) => acc + g.quantidade, 0);
  const pesoTotal = arredondar(
    grupos.reduce((acc, g) => acc + g.quantidade * g.peso, 0),
    3
  );
  const volumeM3 = arredondar(
    grupos.reduce(
      (acc, g) => acc + (g.quantidade * g.altura * g.largura * g.comprimento) / 1e6,
      0
    ),
    6
  );
  return { grupos, qtdVolumes, pesoTotal, volumeM3, linhasValidas };
}

export function descreverCaixa(caixa) {
  const n = normalizarCaixa(caixa);
  return `${n.quantidade}× ${formatarNumeroBR(n.altura, 0)}×${formatarNumeroBR(
    n.largura,
    0
  )}×${formatarNumeroBR(n.comprimento, 0)} cm · ${formatarNumeroBR(n.peso, 2)} kg`;
}

export function resumoTotais(totais) {
  if (!totais || !totais.qtdVolumes) return "";
  const caixas = totais.qtdVolumes === 1 ? "1 volume" : `${totais.qtdVolumes} volumes`;
  return `${caixas} · ${formatarNumeroBR(totais.pesoTotal, 2)} kg · ${formatarNumeroBR(
    totais.volumeM3,
    4
  )} m³`;
}

export function observacaoModoCalculo(modo) {
  return modo === "pelo-total" ? OBSERVACAO_PELO_TOTAL : null;
}
